// D-348 - "העברת לוגים להתקשרות אחרת": the pure part of the dialog (no React).

export type MoveTaskMode = 'existing' | 'new';

export interface MovableLog {
  id: string;
  organization_id?: string | null;
  engagement_id?: string | null;
  ticket_id?: string | null;
  ticket_title?: string | null;
  duration_minutes?: number | null;
  /** for the row in the dialog (optional) */
  start_time?: string | null;
  agent_name?: string | null;
  summary?: string | null;
}

export interface MoveForm {
  targetEngagementId: string;
  taskMode: MoveTaskMode;
  targetTicketId: string;
  newTicketTitle: string;
  reason: string;
}

export const CHANGE_REQUEST_REQUIRED = 'CHANGE_REQUEST_REQUIRED';
export const CHANGE_REQUEST_TYPE = 'work_log_move' as const;

/** The body of POST /api/work-logs/move (and /move/preview). */
export function movePayload(logs: MovableLog[], f: MoveForm) {
  return {
    work_log_ids: Array.from(new Set(logs.map((l) => l.id))),
    target_engagement_id: f.targetEngagementId,
    target_ticket_id: f.taskMode === 'existing' && f.targetTicketId ? f.targetTicketId : null,
    new_ticket_title: f.taskMode === 'new' ? (f.newTicketTitle.trim() || null) : null,
    reason: f.reason.trim() || null,
  };
}

/**
 * The body of POST /api/change-requests (FIX-EDIT-AFTER-LOCK): type work_log_move, payload = the
 * moveWorkLogs parameters (camelCase, as the service takes them), reason.
 */
export function changeRequestPayload(logs: MovableLog[], f: MoveForm) {
  const p = movePayload(logs, f);
  return {
    type: CHANGE_REQUEST_TYPE,
    payload: {
      workLogIds: p.work_log_ids,
      targetEngagementId: p.target_engagement_id,
      targetTicketId: p.target_ticket_id,
      newTicketTitle: p.new_ticket_title,
      reason: p.reason,
    },
    reason: f.reason.trim(),
  };
}

/** The one customer of the selection (null: none, or more than one - a move stays inside one customer). */
export function singleCustomer(logs: MovableLog[]): string | null {
  const ids = new Set(logs.map((l) => l.organization_id ?? ''));
  if (ids.size !== 1) return null;
  const [only] = Array.from(ids);
  return only || null;
}

export function totalMinutes(logs: MovableLog[]): number {
  return logs.reduce((s, l) => s + (Number(l.duration_minutes) || 0), 0);
}

/** The default name of the new task: the task of the logs (when they all share one). */
export function defaultNewTitle(logs: MovableLog[]): string {
  const titles = new Set(logs.map((l) => (l.ticket_title ?? '').trim()).filter(Boolean));
  return titles.size === 1 ? Array.from(titles)[0] : '';
}

/** What is missing before the move can be sent (i18n keys under moveLogs.errors). */
export function formError(f: MoveForm, asRequest: boolean): string | null {
  if (!f.targetEngagementId) return 'engagement';
  if (f.taskMode === 'existing' && !f.targetTicketId) return 'task';
  if (asRequest && f.reason.trim().length < 3) return 'reason';
  return null;
}

/** A 409 CHANGE_REQUEST_REQUIRED from the server (the employee's log already has a report). */
export function isChangeRequestRequired(err: unknown): boolean {
  const e = err as { error?: { error_code?: string }; response?: { data?: { error?: { error_code?: string } } } } | null;
  return (e?.error?.error_code ?? e?.response?.data?.error?.error_code) === CHANGE_REQUEST_REQUIRED;
}

/** FIX-MOVE-LOGS-SELECT: the logs of `logs` whose id is checked (order kept). */
export function checkedLogs<T extends { id: string }>(logs: T[], checked: ReadonlySet<string>): T[] {
  return logs.filter((l) => checked.has(l.id));
}

/** FIX-MOVE-LOGS-SELECT: nothing checked = nothing to move. */
export function canMove(logs: MovableLog[], checked: ReadonlySet<string>): boolean {
  return checkedLogs(logs, checked).length > 0;
}

/**
 * FIX-MOVE-LOGS-SELECT: what the button of a list hands to the dialog - the checked logs when there are any,
 * otherwise every log the user may move (the dialog then opens with all of them checked).
 */
export function logsToMove<T extends { id: string }>(allowed: T[], checked: ReadonlySet<string>): T[] {
  const picked = checkedLogs(allowed, checked);
  return picked.length > 0 ? picked : allowed;
}
